import React, { useState, useEffect, useCallback } from "react";
import { PAGE_SIZE } from "../../../api";
import Icon from "../../../components/Icon";
import Modal from "../../../components/Modal";
import { EmptyState } from "../../../components/State";
import DataTable from "../../../components/DataTable";
import SearchInput from "../../../components/SearchInput";
import {
  ChartCard,
  BarChartComponent,
  AreaTimeSeriesChart,
  DonutChart,
  CHART_THEME_COLORS,
} from "../../../components/Charts";
import { RCAPanel } from "../../../components/TicketDetailModal";
import {
  fetchRcaCharts,
  fetchRcaRecords,
  fetchRcaStats,
  getRcaFilterOptions,
} from "../services/RCAService";

const VALIDATION_CHART_COLORS = {
  Validated: "#16A34A",
  "Auto-validated": "#2563EB",
  "Pending review": "#D97706",
  Rejected: "#DC2626",
};

const VALIDATION_MODIFIERS = {
  Validated: "success",
  "Auto-validated": "info",
  "Pending review": "warning",
  Rejected: "danger",
};

const EMPTY_FILTERS = { search: "", issueType: "", validation: "", autoResolution: "" };

function ValidationBadge({ value }) {
  const label = value || "—";
  const modifier = VALIDATION_MODIFIERS[value] || "neutral";
  return <span className={`rca-pill rca-pill--${modifier}`}>{label}</span>;
}

function ResolutionBadge({ value }) {
  return value === "Yes" ? (
    <span className="rca-pill rca-pill--success">Auto</span>
  ) : (
    <span className="rca-pill rca-pill--neutral">Manual</span>
  );
}

function ConfidenceCell({ value }) {
  const numericValue = Number(value);
  const safeValue = Number.isFinite(numericValue) ? numericValue : 0;
  const level = safeValue >= 85 ? "high" : safeValue >= 70 ? "medium" : "low";
  return (
    <div className="rca-confidence">
      <span className="rca-confidence__track">
        <span
          className={`rca-confidence__fill rca-confidence__fill--${level}`}
          style={{ width: `${Math.max(0, Math.min(100, safeValue))}%` }}
        />
      </span>
      <span className={`rca-confidence__value rca-confidence__value--${level}`}>
        {Number.isFinite(numericValue) ? `${safeValue}%` : "—"}
      </span>
    </div>
  );
}

export default function RCAReportView() {
  const [records, setRecords] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);

  const [stats, setStats] = useState({
    total: 0,
    avg_confidence: 0,
    auto_resolvable: 0,
    auto_resolvable_pct: 0,
    validated: 0,
    rejected: 0,
  });
  const [charts, setCharts] = useState({
    issueTypes: [],
    confidence: [],
    overTime: [],
    validation: [],
  });
  const [options, setOptions] = useState({ issueTypes: [], validations: [] });

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);

  const loadAggregates = useCallback(async () => {
    try {
      const [statsData, chartsData, optionsData] = await Promise.all([
        fetchRcaStats(),
        fetchRcaCharts(),
        getRcaFilterOptions(),
      ]);
      if (statsData) setStats(statsData);
      if (chartsData) setCharts(chartsData);
      if (optionsData) setOptions(optionsData);
    } catch (e) {
      console.error("Could not load RCA aggregates:", e);
    }
  }, []);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchRcaRecords({ page, pageSize: PAGE_SIZE, filters: appliedFilters });
      setRecords(data?.items || []);
      setTotalCount(data?.total || 0);
    } catch {
      setError("Could not load RCA records.");
    } finally {
      setLoading(false);
    }
  }, [page, appliedFilters]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  useEffect(() => {
    loadAggregates();
  }, [loadAggregates]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (appliedFilters.search !== filters.search) {
        setAppliedFilters((prev) => ({ ...prev, search: filters.search }));
        setPage(1);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [filters.search, appliedFilters.search]);

  const handleSelectChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setAppliedFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const hasActiveFilter =
    appliedFilters.search ||
    appliedFilters.issueType ||
    appliedFilters.validation ||
    appliedFilters.autoResolution;

  const statCards = [
    { key: "total", label: "Total RCAs", value: stats.total, icon: "search", tone: "info" },
    {
      key: "confidence",
      label: "Avg confidence",
      value: `${stats.avg_confidence}%`,
      icon: "info",
      tone: "primary",
    },
    {
      key: "auto",
      label: "Auto-resolvable",
      value: `${stats.auto_resolvable_pct}%`,
      icon: "check",
      tone: "success",
    },
    {
      key: "validated",
      label: "Validated",
      value: stats.validated,
      icon: "check",
      tone: "info",
    },
    {
      key: "rejected",
      label: "Rejected",
      value: stats.rejected,
      icon: "alert",
      tone: "danger",
    },
  ];

  const rcaColumns = [
    {
      header: "RCA ID",
      key: "rca_id",
      tip: "Root cause analysis identifier",
      render: (val) => (
        <span
          style={{
            fontFamily: "var(--font-mono, monospace)",
            background: "#F1F5F9",
            border: "1px solid #E2E8F0",
            padding: "2px 6px",
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 600,
            color: "#475569",
            whiteSpace: "nowrap",
          }}
        >
          {val}
        </span>
      ),
    },
    {
      header: "Incident",
      key: "incident_id",
      tip: "Linked incident",
      render: (val, row) => (
        <div>
          <span
            style={{
              fontFamily: "var(--font-mono, monospace)",
              fontSize: 12,
              fontWeight: 600,
              color: "#0F172A",
              whiteSpace: "nowrap",
            }}
          >
            {val || "—"}
          </span>
          {row.ticket_title && (
            <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>
              {row.ticket_title}
            </div>
          )}
        </div>
      ),
    },
    {
      header: "Root Cause",
      key: "ai_root_cause",
      tip: "AI-diagnosed root cause",
      style: { maxWidth: 300 },
      render: (val, row) => (
        <div className="rca-cause-cell">
          <div className="rca-cause-cell__title" style={{ fontWeight: 500, color: "#1E293B", fontSize: 12.5 }}>
            {val || "—"}
          </div>
          {row.infrastructure_dependency && (
            <div className="rca-cause-cell__sub" style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>
              {row.infrastructure_dependency}
            </div>
          )}
        </div>
      ),
    },
    {
      header: "Confidence",
      key: "confidence_pct",
      width: 140,
      tip: "AI confidence in the diagnosis",
      render: (val, row) => <ConfidenceCell value={val ?? row.confidence} />,
    },
    {
      header: "Resolution",
      key: "automated_resolution_available",
      width: 110,
      tip: "Whether automated resolution is available",
      render: (val) => <ResolutionBadge value={val} />,
    },
    {
      header: "Validation",
      key: "validation_status",
      width: 130,
      tip: "Validation outcome",
      render: (val) => <ValidationBadge value={val} />,
    },
    {
      header: "Detected",
      key: "detection_timestamp",
      tip: "When the incident was detected",
      render: (val, row) => {
        const d = val || row.detected_at;
        return (
          <span style={{ fontSize: 12, color: "#64748B", whiteSpace: "nowrap" }}>
            {d ? new Date(d).toLocaleDateString("en-IN") : "—"}
          </span>
        );
      },
    },
  ];

  return (
    <div className="rca-report">
      <div className="rca-report__toolbar">
        <div className="rca-stat-grid">
          {statCards.map((card) => (
            <div key={card.key} className={`rca-stat-card rca-stat-card--${card.tone}`}>
              <span className="rca-stat-card__icon">
                <Icon name={card.icon} size={15} />
              </span>
              <span className="rca-stat-card__body">
                <span className="rca-stat-card__label">{card.label}</span>
                <span className="rca-stat-card__value">{card.value}</span>
              </span>
            </div>
          ))}
        </div>

        <div className="rca-filters">
          <SearchInput
            placeholder="Search by RCA ID, incident, or root cause…"
            value={filters.search}
            onChange={(val) => setFilters((prev) => ({ ...prev, search: val }))}
            style={{ flex: 1, minWidth: 220 }}
          />

          <select
            className="rca-filters__select"
            value={filters.issueType}
            onChange={(e) => handleSelectChange("issueType", e.target.value)}
          >
            <option value="">All issue types</option>
            {options.issueTypes.map((issue) => (
              <option key={issue} value={issue}>
                {issue}
              </option>
            ))}
          </select>

          <select
            className="rca-filters__select"
            value={filters.validation}
            onChange={(e) => handleSelectChange("validation", e.target.value)}
          >
            <option value="">All validation</option>
            {options.validations.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>

          <select
            className="rca-filters__select"
            value={filters.autoResolution}
            onChange={(e) => handleSelectChange("autoResolution", e.target.value)}
          >
            <option value="">Auto &amp; manual</option>
            <option value="Yes">Auto-resolvable</option>
            <option value="No">Manual</option>
          </select>

          {hasActiveFilter && (
            <button type="button" className="rca-filters__clear" onClick={clearFilters}>
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="rca-report__body">
        <div className="rca-chart-row rca-chart-row--wide-left">
          <ChartCard title="Issue breakdown" subtitle="RCAs grouped by reported issue" height={0}>
            <BarChartComponent
              data={charts.issueTypes}
              layout="vertical"
              dataKey="count"
              categoryKey="name"
              barColor={CHART_THEME_COLORS.primary}
              barSize={13}
              categoryWidth={150}
              margin={{ left: 8, right: 16 }}
              emptyMessage="No issue records found for this period."
            />
          </ChartCard>

          <ChartCard title="Validation outcomes" subtitle="How RCAs were validated" height={0}>
            <DonutChart
              data={(charts.validation || []).map((entry) => ({
                name: entry.name,
                value: entry.value,
                color: VALIDATION_CHART_COLORS[entry.name] || CHART_THEME_COLORS.axis,
              }))}
              innerRadius={45}
              outerRadius={75}
              paddingAngle={2}
              height={210}
              emptyMessage="No validation data available yet."
            />
          </ChartCard>
        </div>

        <div className="rca-chart-row rca-chart-row--wide-right">
          <ChartCard title="Confidence distribution" subtitle="AI confidence, bucketed" height={0}>
            <BarChartComponent
              data={charts.confidence}
              layout="horizontal"
              dataKey="count"
              categoryKey="bucket"
              barColor={CHART_THEME_COLORS.success}
              barSize={26}
              emptyMessage="No confidence distribution data available yet."
            />
          </ChartCard>

          <ChartCard title="RCA volume over time" subtitle="Records per day" height={0}>
            <AreaTimeSeriesChart
              data={charts.overTime}
              dataKey="count"
              dateKey="date"
              color={CHART_THEME_COLORS.primary}
              gradientId="rcaVolumeFill"
              emptyMessage="No time-series data available yet."
            />
          </ChartCard>
        </div>

        <DataTable
          columns={rcaColumns}
          data={records}
          keyField="rca_id"
          loading={loading}
          error={error}
          onRetry={loadRecords}
          emptyTitle="No RCA records found"
          emptySubtitle="Try adjusting your filters."
          emptyIcon="search"
          onRowClick={(record) => setSelected(record)}
          page={page}
          total={totalCount}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          itemLabel="RCA records"
        />
      </div>

      {selected && (
        <Modal title="Root Cause Analysis" onClose={() => setSelected(null)} width={680}>
          <div className="rca-modal-head">
            <div className="rca-modal-head__tags">
              <span className="rca-id-chip">{String(selected.rca_id)}</span>
              <ResolutionBadge value={selected.automated_resolution_available} />
              <ValidationBadge value={selected.validation_status} />
            </div>
            <div className="rca-modal-head__title">
              {selected.ticket_title || selected.ai_root_cause}
            </div>
          </div>
          {selected.rca ? (
            <RCAPanel rca={selected.rca} />
          ) : (
            <EmptyState
              icon="search"
              title="No RCA available"
              subtitle="Root cause analysis hasn't been run for this ticket."
            />
          )}
        </Modal>
      )}
    </div>
  );
}
